import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ROLES = [
    { v: "buyer", label: "Buyer / Tenant", desc: "Browse, save & inquire on properties", group: "Customer" },
    { v: "seller", label: "Seller / Owner", desc: "List your own property for sale or rent", group: "Property Owner" },
    { v: "broker", label: "Broker", desc: "List properties, manage leads (RERA)", group: "Real Estate" },
    { v: "advocate", label: "Advocate", desc: "Offer property legal services", group: "Services" },
    { v: "loan_agent", label: "Loan Agent", desc: "Offer home-loan services", group: "Services" },
    { v: "architect", label: "Architect", desc: "Offer architectural design services", group: "Services" },
    { v: "interior_designer", label: "Interior Designer", desc: "Offer interior design services", group: "Services" },
    { v: "civil_contractor", label: "Civil Contractor", desc: "Construction & contracting services", group: "Services" },
    { v: "civil_engineer", label: "Civil Engineer", desc: "Structural design & site engineering", group: "Services" },
    { v: "property_valuer", label: "Property Valuer / Surveyor", desc: "Valuation reports for banks & buyers", group: "Services" },
    { v: "renovation", label: "Renovation / Construction", desc: "Home & office renovation services", group: "Services" },
    { v: "builder", label: "Builder / Developer", desc: "Launch and market new projects (RERA)", group: "Real Estate" },
    { v: "gpm_partner", label: "🤝 GPM Partner", desc: "Share GPM properties & services and build your property business (₹1,000 package)", group: "Partner Program", highlight: true },
];

export default function Register() {
    const [params] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "", city: "", bio: "", referral_code: "" });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        const ref = params.get("ref");
        if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
        // Deep-link role preselection (e.g., /refer → ?role=gpm_partner)
        const preselectedRole = params.get("role");
        if (preselectedRole) {
            setForm((f) => (f.role ? f : { ...f, role: preselectedRole }));
        }
        // If the user landed here via "Post Service" CTA, prime a hint AND (if possible) a default service role.
        const intent = params.get("intent");
        if (intent === "service") setForm((f) => (f.role ? f : { ...f, role: "loan_agent" }));
    }, [params]);
    const intent = params.get("intent");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.role) {
            setErr("Please select your role to continue.");
            return;
        }
        // Client-side email + Indian mobile validation for a better UX.
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((form.email || "").trim());
        if (!emailOk) {
            setErr("Please enter a valid email address.");
            return;
        }
        const digits = (form.phone || "").replace(/\D/g, "").slice(-10);
        if (!/^[6-9]\d{9}$/.test(digits)) {
            setErr("Please enter a valid 10-digit Indian mobile number (starts with 6-9).");
            return;
        }
        setLoading(true);
        const res = await register({ ...form, phone: digits });
        setLoading(false);
        if (!res.ok) {
            setErr(res.error);
            return;
        }
        const next = params.get("next");
        if (res.needsVerification) {
            // GPM Partner: after email verification, route to /partner/activate for payment
            const nextTarget = form.role === "gpm_partner" ? "/partner/activate" : next;
            nav("/verify-otp", { state: { email: res.email, message: res.message, next: nextTarget } });
            return;
        }
        // Direct entry (unlikely — verification is required for all new signups)
        if (form.role === "gpm_partner") {
            nav("/partner/activate");
            return;
        }
        nav(next || "/dashboard");
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-navy-900 grid-pattern px-6 py-12">
            <div className="w-full max-w-2xl">
                <Link to="/" className="flex items-center gap-3 justify-center mb-8">
                    <div className="w-28 h-28 flex items-center justify-center">
                        <img
                            src="/gpm-logo.png"
                            alt="GPM"
                            className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(218,165,32,0.5)]"
                        />
                    </div>
                    <div className="text-left">
                        <div className="font-heading font-bold text-white text-xl leading-tight">Global Property Mart</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-gold-500/90">One Platform · Every Need</div>
                    </div>
                </Link>
                <div className="rounded-3xl glass p-8 gold-border-glow">
                    <h1 className="font-heading text-3xl font-bold text-white">Create your account</h1>
                    <p className="text-white/60 text-sm mt-1">Join India&apos;s most trusted property ecosystem.</p>
                    {intent === "service" && (
                        <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3" data-testid="register-service-banner">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-extrabold">Service Professional Signup</div>
                            <p className="text-sm text-white/85 leading-relaxed mt-1">
                                Register as a <strong>Loan Agent, Advocate, Architect, Interior Designer, Civil Engineer, Contractor</strong> or any other service pro. Pick your exact category below.
                            </p>
                        </div>
                    )}

                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
                        <div>
                            <label className="text-xs text-white/60 mb-2 block">I am a… <span className="text-gold-500">*</span> <span className="text-white/40 normal-case">(required)</span></label>
                            <div className="grid grid-cols-2 gap-2">
                                {ROLES.map((r) => (
                                    <button
                                        key={r.v} type="button"
                                        data-testid={`role-${r.v}`}
                                        onClick={() => setForm({...form, role: r.v})}
                                        className={`text-left p-3 rounded-md border transition-colors ${
                                            form.role === r.v
                                                ? (r.highlight ? "border-gold-500 bg-gold-500/20 ring-1 ring-gold-500/50" : "border-gold-500 bg-gold-500/10")
                                                : (r.highlight ? "border-gold-500/60 bg-gold-500/5 hover:border-gold-500" : "border-white/10 hover:border-gold-500/40")
                                        } ${r.highlight ? "col-span-2" : ""}`}
                                    >
                                        <div className="text-sm font-semibold text-white">{r.label}</div>
                                        <div className="text-[11px] text-white/55 mt-0.5">{r.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Full name" testid="reg-name" required value={form.name} onChange={(v) => setForm({...form, name:v})} />
                            <Input label="City" testid="reg-city" value={form.city} onChange={(v) => setForm({...form, city:v})} />
                        </div>
                        <Input label="Email" type="email" testid="reg-email" required value={form.email} onChange={(v) => setForm({...form, email:v})} />
                        <Input label="Phone (10-digit Indian mobile)" testid="reg-phone" required value={form.phone} onChange={(v) => setForm({...form, phone: v.replace(/[^0-9+]/g, "").slice(0, 15)})} placeholder="9876543210" />
                        <Input label="Password (min 6 chars)" type="password" testid="reg-password" required value={form.password} onChange={(v) => setForm({...form, password:v})} />
                        {["loan_agent", "advocate", "architect", "interior_designer", "civil_engineer", "civil_contractor", "property_valuer", "renovation"].includes(form.role) && (
                            <label className="block">
                                <div className="text-xs uppercase tracking-widest text-white/60 mb-1 font-bold">About your services</div>
                                <textarea
                                    value={form.bio}
                                    onChange={(e) => setForm({...form, bio: e.target.value})}
                                    rows={3}
                                    placeholder="e.g. 10 years of experience designing modern homes and office spaces in Nagpur. Specialize in modular kitchens, wardrobes, false ceiling."
                                    data-testid="reg-bio"
                                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500/60 resize-none placeholder:text-white/40"
                                />
                                <div className="text-[11px] text-white/45 mt-1">This appears on your public profile card and helps buyers pick you.</div>
                            </label>
                        )}
                        <Input label="Referral code (optional)" testid="reg-referral" value={form.referral_code} onChange={(v) => setForm({...form, referral_code:v.toUpperCase()})} />

                        {err && <div className="text-sm text-red-400" data-testid="register-error">{err}</div>}
                        <button
                            type="submit" disabled={loading}
                            data-testid="register-submit"
                            className="w-full px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-60"
                        >{loading ? "Creating…" : "Create account"}</button>
                    </form>

                    {/* Email confirmation CTA — post-registration touchpoint */}
                    <a
                        href="mailto:globalpropertymart95@gmail.com?subject=Help%20verifying%20my%20new%20account&body=Hi%20Team%2C%0A%0AI%20just%20registered%20on%20Global%20Property%20Mart.%20My%20email%3A%20"
                        data-testid="register-email-cta"
                        className="mt-5 flex items-center gap-3 p-4 rounded-xl bg-gold-500/10 border border-gold-500/40 hover:bg-gold-500/15 hover:border-gold-500/60 transition-colors group"
                    >
                        <div className="w-10 h-10 rounded-full bg-gold-500 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                            <Mail className="w-5 h-5 text-navy-900" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white">Need help verifying your account?</div>
                            <div className="text-xs text-white/65 mt-0.5">globalpropertymart95@gmail.com · Reply within 24 hours</div>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest font-bold text-gold-500 shrink-0">Email</span>
                    </a>

                    <div className="mt-6 text-center text-sm text-white/60">
                        Already have an account? <Link to={`/login${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="text-gold-500 font-medium">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Input({ label, testid, type = "text", value, onChange, required }) {
    return (
        <div>
            <label className="text-xs text-white/60 mb-1 block">{label}</label>
            <input
                type={type} required={required}
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/40"
            />
        </div>
    );
}
